import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/request';
import type {
  WorkflowRule,
  WorkflowRuleListResponse,
} from '@/pages/ats-job-creation/tabs/interview-plan/stage/automation/api/workflowRule.types';
import { deriveStageAutomation } from './deriveStageAutomation';

// Per-stage cache key — same shape used by the interview-plan drawer's
// `useGetStageAutomation`, so the menu fetches piggyback on whatever
// the drawer has already warmed (and vice versa).
// How long a fetched stage rule counts as fresh. The rule only changes when
// someone saves or deletes the automation, and both invalidate this key
// (`useStageAutomation`), so caching it is safe — and it is what makes warming
// the cache from the stage dropdown actually save the confirm modal a call.
export const STAGE_RULE_STALE_TIME_MS = 30_000;

const stageRuleKey = (stageId: string) =>
  ['workflowRule', 'stage', stageId] as const;

// Single-stage fetch — filters by `ats_job_stage_id`. Used by the inline
// preview / chip for stages that aren't in the page's interview-plan list
// (transfer / add-to-job target another job's stages; the candidate-drawer
// stage chip). Marker surfaces instead read `stage.automation` from the
// stages they already loaded — see `deriveStageAutomation`.
const fetchActiveStageRule = async (
  stageId: string
): Promise<WorkflowRule | null> => {
  const res = await api.get<WorkflowRuleListResponse>('workflow_rules', {
    params: {
      event_name: 'application.stage_changed',
      ats_job_stage_id: Number(stageId),
      status: 'active',
    },
  });
  return res.workflow_rules?.[0] ?? null;
};

type Options = { enabled?: boolean };

// A job's stages carry a per-stage `automation` field (`_ats_job_stage.json
// .jbuilder` → `json.automation`), so one `GET` gives every stage's rule
// presence at once — no per-stage `workflow_rules` round-trips.
type JobStageWithAutomation = {
  id?: string | number | null;
  automation?: { running_count?: number } | null;
};

const fetchJobAutomatedStageIds = async (
  jobId: string | number
): Promise<Set<string>> => {
  // `ats_job_stages#index` is a raw jbuilder array (no `{ data }` envelope).
  const stages = await api.get<JobStageWithAutomation[]>(
    `ats_jobs/${jobId}/ats_job_stages`
  );
  return deriveStageAutomation(Array.isArray(stages) ? stages : []).stageIds;
};

// The set of stage ids (stringified) that have an active stage-automation rule
// for a whole job — drives the ⚡ markers in a stage dropdown whose target job
// isn't the current page (add-to-job / transfer / add-to-additional-job). For
// stages the page already loaded, use `deriveStageAutomation` directly instead.
export const useJobAutomatedStageIds = (
  jobId: string | number | undefined | null,
  options: Options = {}
) => {
  const enabled = (options.enabled ?? true) && Boolean(jobId);
  return useQuery<Set<string>, Error>({
    queryKey: ['jobAutomatedStageIds', String(jobId ?? '')],
    queryFn: () => fetchJobAutomatedStageIds(jobId as string | number),
    enabled,
    staleTime: 0,
  });
};

// Single-stage variant. Same cache key as the interview-plan drawer's
// `useGetStageAutomation`, so the inline-preview surfaces share fetches —
// opening the dropdown warms the cache; picking a stage resolves the rule
// from cache without another network round-trip.
export const useStageAutomationRule = (
  stageId: string | number | undefined | null,
  options: Options = {}
) => {
  const enabled = (options.enabled ?? true) && Boolean(stageId);
  const stageIdString =
    stageId === null || stageId === undefined ? '' : String(stageId);
  return useQuery<WorkflowRule | null, Error>({
    queryKey: stageRuleKey(stageIdString),
    queryFn: () => fetchActiveStageRule(stageIdString),
    enabled,
    staleTime: STAGE_RULE_STALE_TIME_MS,
  });
};

// Delete a workflow_rule by id and invalidate the matching stage's
// rule cache so any consumer subscribed to `stageRuleKey(stageId)` —
// the inline preview, the candidate-drawer stage chip — reflects the
// removal on the next render. Mirrors the interview-plan drawer's
// `useDeleteStageAutomation` but exposed at the shared-component layer
// for cross-page consumers.
export const useDeleteStageRule = () => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { ruleId: number; stageId: string | number }>(
    {
      mutationFn: ({ ruleId }) => api.delete<void>(`workflow_rules/${ruleId}`),
      onSuccess: (_void, { stageId }) => {
        queryClient.invalidateQueries({
          queryKey: stageRuleKey(String(stageId)),
        });
      },
    }
  );
};
